import { useCallback, useEffect, useRef, useState } from 'react';

import type { Match, Team, TeamId } from '@/core/schemas';

/**
 * Watches matches and queues walk-on entrances for teams as they are called up.
 *
 * When a match newly enters `called`, both its teams are enqueued. Matches that
 * are already `called` on first render are marked as seen, not replayed — so
 * opening the beamer view mid-tournament does not trigger a burst of overlays.
 */
export function useWalkOnQueue(
  matches: readonly Match[],
  lookup: (teamId: TeamId) => Team | undefined,
  ready: boolean,
): { current: Team | null; dismiss: () => void; enqueue: (teamId: TeamId) => void } {
  const seen = useRef<Set<string> | null>(null);
  const [queue, setQueue] = useState<Team[]>([]);

  const enqueue = useCallback(
    (teamId: TeamId) => {
      const team = lookup(teamId);
      if (team !== undefined) {
        setQueue((current) => [...current, team]);
      }
    },
    [lookup],
  );

  useEffect(() => {
    // Wait for the first loaded snapshot: initialising the seen-set while the
    // data is still empty would make every already-called match look "new" once
    // it arrives, replaying entrances when the beamer is opened mid-tournament.
    if (!ready) {
      return;
    }

    const called = matches.filter((match) => match.status === 'called');

    // First loaded pass: remember what is already called without announcing it.
    if (seen.current === null) {
      seen.current = new Set(called.map((match) => match.id));
      return;
    }

    const fresh = called.filter((match) => !seen.current?.has(match.id));

    if (fresh.length === 0) {
      return;
    }

    const teams: Team[] = [];
    for (const match of fresh) {
      seen.current.add(match.id);
      for (const side of [match.home, match.away]) {
        if (side.kind === 'team') {
          const team = lookup(side.teamId);
          if (team !== undefined) {
            teams.push(team);
          }
        }
      }
    }

    if (teams.length > 0) {
      // Bridging an external, changing list (Firestore matches) into a local
      // queue is the sanctioned "external system → state" case; the update only
      // runs when genuinely new call-ups appear, so it cannot loop.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQueue((current) => [...current, ...teams]);
    }
  }, [matches, lookup, ready]);

  const dismiss = useCallback(() => {
    setQueue((current) => current.slice(1));
  }, []);

  return { current: queue[0] ?? null, dismiss, enqueue };
}
